import React, { useState, useEffect } from 'react';
import { X, Lock, Unlock, RefreshCw, UserCog } from 'lucide-react';
import { toast } from 'react-toastify';
import { usersAPI } from '../api/users';
import { getBranches } from '../api/branches';

const EditUserModal = ({ isOpen, onClose, user, onUpdate, currentUserRole }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    role: ''
  });
  const [loading, setLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [manageableRoles, setManageableRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [roleChangeLoading, setRoleChangeLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
        role: user.role || ''
      });
      setSelectedRole(user.role || '');
      setSelectedBranch(user.current_branch_id?.toString() || '');
    }
  }, [user]);

  // Fetch manageable roles and branches
  useEffect(() => {
    const fetchRolesAndBranches = async () => {
      try {
        const [rolesRes, branchesRes] = await Promise.all([
          usersAPI.getManageableRoles(),
          getBranches()
        ]);
        setManageableRoles(rolesRes.data?.manageableRoles || []);
        const branchList = branchesRes?.data || branchesRes || [];
        setBranches(Array.isArray(branchList) ? branchList : []);
      } catch (error) {
        console.error('Failed to fetch roles/branches:', error);
      }
    };
    if (isOpen) {
      fetchRolesAndBranches();
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onUpdate(user.id, formData);
      toast.success('User updated successfully');
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm('Are you sure you want to reset this user\'s password? A new temporary password will be generated.')) {
      return;
    }

    setLoading(true);
    try {
      const response = await onUpdate(user.id, null, 'reset-password');

      // Show the temporary password
      if (response.data?.temporary_password) {
        setShowResetPassword(true);
        toast.success('Password reset successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLock = async () => {
    const action = user.is_locked ? 'unlock' : 'lock';
    const reason = !user.is_locked ? prompt('Enter reason for locking this user:') : null;

    if (!user.is_locked && !reason) return;

    setLoading(true);
    try {
      await onUpdate(user.id, null, action, reason);
      toast.success(`User ${action}ed successfully`);
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.error || `Failed to ${action} user`);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async () => {
    if (!selectedRole || selectedRole === user.role) {
      toast.info('Please select a different role');
      return;
    }

    // Branch-level roles require branch
    if (['branch_admin', 'branch_staff'].includes(selectedRole) && !selectedBranch) {
      toast.error('Please select a branch for this role');
      return;
    }

    if (!window.confirm(`Are you sure you want to change ${user.full_name}'s role to ${formatRoleName(selectedRole)}?`)) {
      return;
    }

    setRoleChangeLoading(true);
    try {
      await usersAPI.changeUserRole(
        user.id,
        selectedRole,
        ['branch_admin', 'branch_staff'].includes(selectedRole) ? parseInt(selectedBranch) : null
      );
      toast.success(`Role changed to ${formatRoleName(selectedRole)}`);
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change role');
    } finally {
      setRoleChangeLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      full_name: '',
      email: '',
      role: ''
    });
    setShowResetPassword(false);
    onClose();
  };

  if (!isOpen || !user) return null;

  // Check if user can be edited
  const isProtected = user.role === 'super_admin';
  const isSsoUser = user.is_sso || user.platform_user_id;
  const canChangeRole = manageableRoles.includes(user.role) && user.role !== 'super_admin';

  const formatRoleName = (role) => {
    const names = {
      super_admin: 'Super Admin',
      system_admin: 'System Admin',
      retail_admin: 'Retail Admin',
      branch_admin: 'Branch Admin',
      branch_staff: 'Branch Staff',
      admin: 'Admin',
      staff: 'Staff'
    };
    return names[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'bg-accent-gold text-primary-black',
      system_admin: 'bg-purple-100 text-purple-800',
      retail_admin: 'bg-blue-100 text-blue-800',
      branch_admin: 'bg-indigo-100 text-indigo-800',
      branch_staff: 'bg-green-100 text-green-800',
      admin: 'bg-blue-100 text-blue-800',
      staff: 'bg-green-100 text-green-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-primary-black">
            Edit User
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {isProtected && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800">
              <strong>Protected Account:</strong> Super Admin accounts cannot be edited.
            </p>
          </div>
        )}

        {isSsoUser && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
             <div className="flex items-start gap-3">
               <div className="bg-blue-100 p-1.5 rounded-full mt-0.5">
                 <Shield className="h-4 w-4 text-blue-600" />
               </div>
               <div>
                  <h4 className="text-sm font-semibold text-blue-900">SSO Managed User</h4>
                  <p className="text-sm text-blue-800 mt-1">
                    This user's profile (Name, Email, Password) is managed by the One Manager Platform and cannot be edited here.
                  </p>
               </div>
             </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* User Avatar and Basic Info */}
          <div className="flex items-center gap-4 pb-4 border-b">
            <div className="h-16 w-16 rounded-full bg-accent-gold flex items-center justify-center text-primary-black font-bold text-2xl">
              {user.full_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{user.full_name}</h3>
              <p className="text-sm text-gray-500">{user.email}</p>
              <p className="text-xs text-gray-400 mt-1">
                Created: {new Date(user.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Role Badge */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Role
            </label>
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getRoleBadgeColor(user.role)}`}>
              {formatRoleName(user.role)}
            </span>
          </div>

          {/* Role Change Section */}
          {canChangeRole && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <UserCog className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Change Role</span>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">New Role</label>
                  <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    disabled={roleChangeLoading}
                  >
                    <option value="">Select role...</option>
                    {manageableRoles.map(role => (
                      <option key={role} value={role}>{formatRoleName(role)}</option>
                    ))}
                  </select>
                </div>

                {['branch_admin', 'branch_staff'].includes(selectedRole) && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Assign to Branch</label>
                    <select
                      value={selectedBranch}
                      onChange={(e) => setSelectedBranch(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      disabled={roleChangeLoading}
                    >
                      <option value="">Select branch...</option>
                      {branches.map(branch => (
                        <option key={branch.id} value={branch.id}>{branch.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleRoleChange}
                  disabled={roleChangeLoading || !selectedRole || selectedRole === user.role}
                  className="w-full px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {roleChangeLoading ? 'Changing...' : 'Apply Role Change'}
                </button>
              </div>
            </div>
          )}

          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent disabled:bg-gray-100"
              placeholder="Enter full name"
              required
              disabled={isProtected || isSsoUser || loading}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent disabled:bg-gray-100"
              placeholder="Enter email address"
              required
              disabled={isProtected || isSsoUser || loading}
            />
          </div>

          {/* Account Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Status
            </label>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                {user.is_locked ? (
                  <>
                    <Lock className="h-4 w-4 text-red-600" />
                    <span className="text-sm text-red-600 font-medium">Locked</span>
                  </>
                ) : (
                  <>
                    <Unlock className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-600 font-medium">Active</span>
                  </>
                )}
              </div>
              {!isProtected && (
                <button
                  type="button"
                  onClick={handleToggleLock}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  disabled={loading}
                >
                  {user.is_locked ? 'Unlock' : 'Lock'} Account
                </button>
              )}
            </div>
          </div>

          {/* Password Reset - Disabled for SSO users */}
          {!isProtected && !isSsoUser && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password Management
              </label>
              <button
                type="button"
                onClick={handleResetPassword}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                <RefreshCw className="h-4 w-4" />
                Reset Password
              </button>
              <p className="mt-2 text-xs text-gray-500">
                This will generate a new temporary password for the user
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            {!isProtected && (
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </form>

        {/* Show temporary password after reset */}
        {showResetPassword && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800 font-medium mb-2">
              New Temporary Password:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-white border border-green-300 rounded text-sm font-mono">
                [Password will be shown here]
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText('[Password]');
                  toast.success('Password copied to clipboard');
                }}
                className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Copy
              </button>
            </div>
            <p className="text-xs text-green-700 mt-2">
              Please provide this password to the user securely.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditUserModal;
